/**
 * Validates redirect URLs against a whitelist to prevent open redirect vulnerabilities.
 *
 * @param redirectUrl - The URL to validate
 * @param allowedDomains - Optional array of additional allowed domains (e.g., partner domains)
 * @returns The validated redirect URL, or null if invalid
 */
export function validateRedirectUrl(
  redirectUrl: string | null | undefined,
  allowedDomains: string[] = []
): string | null {
  if (!redirectUrl) {
    return null;
  }

  // Remove whitespace
  const trimmedUrl = redirectUrl.trim();
  if (!trimmedUrl) {
    return null;
  }

  // Allow relative paths (internal routes)
  if (trimmedUrl.startsWith('/') && !trimmedUrl.startsWith('//')) {
    return trimmedUrl;
  }

  try {
    const url = new URL(trimmedUrl);

    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(url.protocol)) {
      return null;
    }

    // Whitelist of allowed domains
    const whitelistedDomains = [
      'app.vectorco.dev',
      'vector.co',
      'app.commonroom.io',
      'app.userled.io',
      'app-stg.userled.io',
      'app.warmly.ai',
      'getwarmly.ai',
      'getwarmly.com',
      'marketbetter.ai',
      'app.usergems.com',
      ...allowedDomains,
    ];

    // Check if the domain (or parent domain) is whitelisted
    const hostname = url.hostname.toLowerCase();
    const isAllowed = whitelistedDomains.some(domain => {
      const normalizedDomain = domain.toLowerCase();
      // Exact match
      if (hostname === normalizedDomain) {
        return true;
      }
      // Subdomain match (e.g., app.example.com matches example.com)
      if (hostname.endsWith('.' + normalizedDomain)) {
        return true;
      }
      return false;
    });

    if (!isAllowed) {
      console.warn(`Redirect URL rejected - domain not whitelisted: ${hostname}`);
      return null;
    }

    return trimmedUrl;
  } catch (error) {
    // Invalid URL format
    console.warn(`Invalid redirect URL format: ${trimmedUrl}`, error);
    return null;
  }
}
